/**
 * CSV Import Composable
 * CSVインポート機能の状態とロジックを管理
 */

import { ref, reactive, computed } from 'vue'
import { MasterApiService } from '@/services/MasterApiService'

export function useCsvImport() {
  // リアクティブな状態
  const state = reactive({
    selectedFile: null,
    selectedMasterType: '',
    previewData: [],
    canImport: false,
    currentPage: 1,
    itemsPerPage: 50
  })

  // CSVインポート用マスタオプション
  const csvImportMasterOptions = [
    { value: 'event', label: '種目マスタ' },
    { value: 'race_type', label: 'イベント格式マスタ' },
    { value: 'event_race_type', label: '種目とイベント格式マスタ' },
    { value: 'race_owner', label: '主催者マスタ' },
    { value: 'class_owner', label: '種別マスタ' },
    { value: 'hall', label: '会場マスタ' },
    { value: 'race_series', label: '大会シリーズマスタ' },
    { value: 'displace', label: '排気量マスタ' }
  ]

  // カラムマッピング定義
  const columnMap = {
    event: [
      { key: 'event_cd', label: '種目コード' },
      { key: 'event_name', label: '種目名' },
      { key: 'event_short_name', label: '種目略称' },
      { key: 'event_view_number', label: '表示番号' },
      { key: 'event_limit_startdate', label: '開催開始日' },
      { key: 'event_limit_enddate', label: '開催終了日' }
    ],
    race_type: [
      { key: 'race_type_cate', label: '格式コード' },
      { key: 'race_type_cate_name', label: '格式名' },
      { key: 'race_type_cd', label: '格式コード' },
      { key: 'race_type_name', label: '格式名' },
      { key: 'race_numer', label: '格式番号' },
      { key: 'point_flg', label: 'ポイントフラグ' }
    ],
    event_race_type: [
      { key: 'event_cd', label: '種目コード' },
      { key: 'race_type_cd', label: '格式コード' },
      { key: 'race_type_name', label: '格式名' },
      { key: 'race_sports_year', label: 'スポーツ年度' }
    ],
    race_owner: [
      { key: 'race_owner_login_id', label: '主催者コード' },
      { key: 'race_owner_name', label: '主催者名' },
      { key: 'race_owner_name_kana', label: '主催者名カナ' },
      { key: 'race_chairman_name', label: '代表者氏名' }
    ],
    race_owner_event: [
      { key: 'race_owner_id', label: '主催者ID' },
      { key: 'event_cd', label: '種目コード' }
    ],
    race_owner_access: [
      { key: 'race_owner_id', label: '主催者ID' },
      { key: 'race_owner_access_cd', label: 'アクセスコード' },
      { key: 'race_owner_access_name', label: 'アクセス名' }
    ],
    class_owner: [
      { key: 'class_owner_kbn_cd', label: '種別区分コード' },
      { key: 'class_owner_kbn_name', label: '種別区分名' },
      { key: 'class_owner_cd', label: '種別コード' },
      { key: 'class_owner_name', label: '種別名' }
    ],
    hall: [
      { key: 'mfj_hall_hall_cd', label: '会場コード' },
      { key: 'mfj_hall_hall_name', label: '会場名' },
      { key: 'mfj_hall_hall_kana', label: '会場名カナ' },
      { key: 'mfj_hall_chairman', label: '代表者氏名' },
      { key: 'mfj_hall_circuit', label: 'サーキット名' }
    ],
    race_series: [
      { key: 'event_cd', label: '種目コード' },
      { key: 'race_type_cd', label: '格式コード' },
      { key: 'race_series_cd', label: 'シリーズコード' },
      { key: 'race_series_name', label: 'シリーズ名' },
      { key: 'view_number', label: '表示番号' }
    ],
    displace: [
      { key: 'event_cd', label: '種目コード' },
      { key: 'displace_cd', label: '排気量コード' },
      { key: 'displace_name', label: '排気量名' },
      { key: 'displace_short_name', label: '排気量略称' },
      { key: 'view_number', label: '表示番号' }
    ]
  }

  // 算出プロパティ
  const paginatedPreviewData = computed(() => {
    const start = (state.currentPage - 1) * state.itemsPerPage
    const end = start + state.itemsPerPage
    return state.previewData.slice(start, end)
  })

  const totalPages = computed(() => {
    return Math.ceil(state.previewData.length / state.itemsPerPage)
  })

  const pagesToShow = computed(() => {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1)
  })

  // ファイル選択処理
  const handleFileUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      state.selectedFile = file
      state.previewData = []
      state.canImport = false
      return {
        success: true,
        message: 'ファイルが選択されました。プレビューボタンを押してください。'
      }
    }
    return {
      success: false,
      message: 'ファイル選択に失敗しました。'
    }
  }

  // CSVプレビュー
  const previewCsv = () => {
    return new Promise((resolve, reject) => {
      if (!state.selectedFile) {
        reject(new Error('ファイルが選択されていません'))
        return
      }

      const selectedMasterValue = csvImportMasterOptions.find(
        type => type.label === state.selectedMasterType
      )?.value

      if (!selectedMasterValue) {
        reject(new Error('マスタ項目が選択されていません'))
        return
      }

      const columns = columnMap[selectedMasterValue] || []
      if (columns.length === 0) {
        reject(new Error(`${state.selectedMasterType} に対応するカラム定義が見つかりませんでした`))
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target.result
          const lines = text.split('\n').filter(line => line.trim() !== '')
          
          if (lines.length === 0) {
            state.previewData = []
            state.canImport = false
            reject(new Error('選択されたCSVファイルにデータがありません'))
            return
          }

          const newPreviewData = lines.map(line => {
            const values = line.split(',')
            const item = {}
            columns.forEach((col, index) => {
              item[col.key] = values[index]?.trim() || ''
            })
            item._master_type = selectedMasterValue
            return item
          })
          
          state.previewData = newPreviewData

          state.canImport = state.previewData.length > 0
          state.currentPage = 1

          resolve({
            success: true,
            message: `${state.previewData.length}件のデータをプレビュー表示しています。内容を確認し、登録ボタンを押してください。`
          })
        } catch (error) {
          reject(error)
        }
      }
      
      reader.onerror = () => {
        reject(new Error('ファイル読み込みに失敗しました'))
      }
      
      reader.readAsText(state.selectedFile)
    })
  }

  // CSVインポート実行
  const importCsv = async () => {
    if (!state.selectedFile || !state.canImport) {
      throw new Error('インポート条件が満たされていません')
    }

    const masterTypeValue = csvImportMasterOptions.find(
      type => type.label === state.selectedMasterType
    )?.value

    if (!masterTypeValue) {
      throw new Error('無効なマスタタイプが選択されています')
    }

    try {
      const csvData = await fileToBase64(state.selectedFile)
      const result = await MasterApiService.importCsv(
        masterTypeValue,
        csvData,
        state.selectedFile.name
      )

      if (result.success) {
        clearImport()
        return {
          success: true,
          message: result.message,
          importedCount: result.importedCount
        }
      }

      return {
        success: false,
        message: result.message
      }
    } catch (error) {
      console.error('CSVインポートエラー:', error)
      return {
        success: false,
        message: 'CSVインポートに失敗しました'
      }
    }
  }

  // インポート用入力のクリア
  const clearImport = () => {
    state.selectedMasterType = ''
    state.selectedFile = null
    state.previewData = []
    state.canImport = false
    state.currentPage = 1

    // ファイル入力要素のクリア
    const fileInput = document.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  // プレビューテーブルのカラム取得
  const getPreviewTableColumns = () => {
    const selectedMasterValue = csvImportMasterOptions.find(
      type => type.label === state.selectedMasterType
    )?.value

    return columnMap[selectedMasterValue] || []
  }

  // ページ移動
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      state.currentPage = page
    }
  }

  // ファイルをBase64に変換
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64String = reader.result.split(',')[1]
        resolve(base64String)
      }
      reader.onerror = error => reject(error)
    })
  }

  return {
    // 状態
    state,
    
    // オプション
    csvImportMasterOptions,
    
    // 算出プロパティ
    paginatedPreviewData,
    totalPages,
    pagesToShow,
    
    // メソッド
    handleFileUpload,
    previewCsv,
    importCsv,
    clearImport,
    getPreviewTableColumns,
    goToPage
  }
}